import { ReactNode, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import EvaluationInfo from "../../components/EvaluationInfo";
import InviteSelector from "../../components/InviteSelector";
import Loader from "../../../../components/ui/Loader";
import {
  createEvaluationInvites,
  updateEvaluator,
} from "../../../../services/evaluationsService";
import { useStartEvaluationData } from "../../hooks/useStartEvaluationData";
import Desktop from "../../../../assets/icons/Desktop.svg";
import Coffee from "../../../../assets/icons/Coffee.svg";
import { Modal } from "@react-solutions-ui/findmore-ui";

import styles from "./SelectColleagues.module.css";
import { WhiteButton } from "@react-solutions-ui/findmore-ui";
import { BlueButton } from "@react-solutions-ui/findmore-ui";
import { IUpdateEvaluatorBody } from "../../Interfaces/IEvaluationInterface";

export type ModalInfo = {
  title: string;
  content: ReactNode;
  action: () => void;
  closeAction: () => void;
  confirmActionLabel: string;
  closeActionLabel: string;
  isSending?: boolean;
  type: "confirm" | "info";
};

export const confirmContentCreateInvites = (
  <div>
    <p>Tem a certeza que quer enviar os convites?</p>
    <p>Antes de enviar, confirma se:</p>
    <ul>
      <li>
        As pessoas escolhidas estão disponíveis (por exemplo, não estão de
        férias 🏖️)
      </li>
      <li>Queres mesmo partilhar esta avaliação com eles.</li>
    </ul>
  </div>
);

export const confirmContentUpdateEvaluator = (
  <div>
    <p>Tem a certeza que quer alterar o(s) colega(s) de avaliação?</p>
    <p>Antes de enviar, confirma se:</p>
    <ul>
      <li>
        As pessoas escolhidas estão disponíveis (por exemplo, não estão de
        férias 🏖️)
      </li>
      <li>Queres mesmo partilhar esta avaliação com eles.</li>
    </ul>
  </div>
);

export default function StartEvaluation() {
  const navigate = useNavigate();
  const {
    userEvaluation,
    employeesMinimal,
    loadingUserEvaluation,
    loadingEmployeesMinimal,
  } = useStartEvaluationData();
  // Get info in the url
  const urlParams = new URLSearchParams(window.location.search);
  const editingWork = urlParams.get("editingWork") === "true";
  const editingSocial = urlParams.get("editingSocial") === "true";

  useEffect(() => {
    if (!loadingUserEvaluation && !userEvaluation) {
      navigate("/error", { replace: true });
    }
    if (
      (!loadingUserEvaluation &&
        userEvaluation?.evaluators?.length !== 0 &&
        !editingWork &&
        !editingSocial) ||
      userEvaluation?.statusReason === 122470005
    ) {
      navigate("/home", { replace: true });
    }
  }, [
    loadingUserEvaluation,
    navigate,
    userEvaluation,
    editingWork,
    editingSocial,
  ]);

  const [selectedWork, setSelectedWork] = useState("");
  const [selectedSocial, setSelectedSocial] = useState("");

  useEffect(() => {
    if (userEvaluation?.evaluators && selectedWork === "") {
      const evaluator = userEvaluation.evaluators.find(
        (evaluator: any) => evaluator.evaluatorTypeChoice === "Colleague"
      );
      if (evaluator) {
        setSelectedWork(evaluator.employeeId);
      }
    }
  }, [userEvaluation, selectedWork, editingWork]);

  useEffect(() => {
    if (userEvaluation?.evaluators && selectedSocial === "") {
      const evaluator = userEvaluation.evaluators.find(
        (evaluator: any) => evaluator.evaluatorTypeChoice === "Friend"
      );
      if (evaluator) {
        setSelectedSocial(evaluator.employeeId);
      }
    }
  }, [userEvaluation, selectedSocial, editingSocial]);

  const [modalInfo, setModalInfo] = useState<ModalInfo | null>(null);

  const handleCreateInvites = async () => {
    try {
      setModalInfo({
        title: "Confirmar Envio de Convites",
        content: confirmContentCreateInvites,
        action: () => {
          return;
        },
        closeAction: () => {
          return;
        },
        confirmActionLabel: "A enviar",
        closeActionLabel: "A enviar",
        isSending: true,
        type: "info",
      });

      const currentEval = userEvaluation?.id ?? "";
      if (!currentEval) {
        return;
      }

      const result = await createEvaluationInvites(
        currentEval,
        selectedWork,
        selectedSocial
      );
      if (result.success) {
        navigate("/home");
      } else {
        throw new Error("Failed to create evaluation invites");
      }
    } catch (err) {
      setModalInfo({
        title: "Erro ao enviar convites",
        content: (
          <p>
            Ocorreu um erro ao enviar os convites. Por favor, tenta novamente.
          </p>
        ),
        action: () => {
          return;
        },
        closeAction: closeModal,
        confirmActionLabel: "OK",
        closeActionLabel: "OK",
        type: "info",
      });
    }
  };

  const handleUpdateEvaluator = async () => {
    try {
      setModalInfo({
        title: "Confirmar Envio de Convites",
        content: confirmContentUpdateEvaluator,
        action: () => {
          return;
        },
        closeAction: () => {
          return;
        },
        confirmActionLabel: "A enviar",
        closeActionLabel: "A enviar",
        isSending: true,
        type: "info",
      });

      const currentEval = userEvaluation?.id ?? "";
      if (!currentEval) {
        return;
      }

      const evaluatorsToRemove: string[] = [];
      const evaluatorsToAdd: { EmployeeId: string; EvaluatorType: number }[] = [];

      if (editingWork) {
        const colleagueEvaluator = userEvaluation.evaluators.find(
          (evaluator: any) => evaluator.evaluatorTypeChoice === "Colleague"
        );
        if (colleagueEvaluator?.id) {
          evaluatorsToRemove.push(colleagueEvaluator.id);
        }
        evaluatorsToAdd.push({
          EmployeeId: selectedWork,
          EvaluatorType: 122470003, // Colleague
        });
      }

      if (editingSocial) {
        const friendEvaluator = userEvaluation.evaluators.find(
          (evaluator: any) => evaluator.evaluatorTypeChoice === "Friend"
        );
        if (friendEvaluator?.id) {
          evaluatorsToRemove.push(friendEvaluator.id);
        }
        evaluatorsToAdd.push({
          EmployeeId: selectedSocial,
          EvaluatorType: 122470002, // Friend / Social
        });
      }

      const updateEvaluatorBody: IUpdateEvaluatorBody = {
        EvaluatorsToRemove: evaluatorsToRemove,
        EvaluatorsToAdd: evaluatorsToAdd,
      };

      const result = await updateEvaluator(currentEval, updateEvaluatorBody);
      if (result.success) {
        navigate("/home");
      } else {
        throw new Error("Failed to update evaluator");
      }
    } catch (err) {
      setModalInfo({
        title: "Erro ao atualizar colega de avaliação",
        content: (
          <p>
            Ocorreu um erro ao atualizar o colega de avaliação. Por favor, tenta
            novamente.
          </p>
        ),
        action: () => {
          return;
        },
        closeAction: closeModal,
        confirmActionLabel: "OK",
        closeActionLabel: "OK",
        type: "info",
      });
    }
  };

  const workEmployeesFiltered = useMemo(() => {
    const allEmployees = employeesMinimal ?? [];
    return allEmployees.filter((emp) => emp.id !== selectedSocial);
  }, [employeesMinimal, selectedSocial]);

  const socialEmployeesFiltered = useMemo(() => {
    const allEmployees = employeesMinimal ?? [];
    return allEmployees.filter((emp) => emp.id !== selectedWork);
  }, [employeesMinimal, selectedWork]);

  const handleModalCreate = () => {
    setModalInfo({
      title:
        editingWork || editingSocial
          ? "Confirmar Alterações"
          : "Confirmar Envio de Convites",
      content:
        editingWork || editingSocial
          ? confirmContentUpdateEvaluator
          : confirmContentCreateInvites,
      action:
        editingWork || editingSocial
          ? handleUpdateEvaluator
          : handleCreateInvites,
      closeAction: closeModal,
      confirmActionLabel: "Enviar",
      closeActionLabel: "Cancelar",
      type: "confirm",
    });
  };

  const handleCloseAndSendLater = () => {
    if (editingWork || editingSocial) {
      const originalWork = userEvaluation?.evaluators?.find(
        (evaluator: any) => evaluator.evaluatorTypeChoice === "Colleague"
      )?.employeeId;
      const originalSocial = userEvaluation?.evaluators?.find(
        (evaluator: any) => evaluator.evaluatorTypeChoice === "Friend"
      )?.employeeId;

      const hasChanged =
        (editingWork && selectedWork !== originalWork) ||
        (editingSocial && selectedSocial !== originalSocial);

      if (!hasChanged) {
        window.location.href = "/home";
        return;
      }
    } else {
      if (!selectedWork && !selectedSocial) {
        window.location.href = "/home";
        return;
      }
    }

    setModalInfo({
      title: "Tens a certeza que queres sair?",
      content:
        editingWork || editingSocial ? (
          <p> Se saires agora, vais perder as alterações.</p>
        ) : (
          <p> Se saires agora, vais perder a seleção de colegas.</p>
        ),
      action: () => {
        window.location.href = "/home";
      },
      closeAction: closeModal,
      confirmActionLabel: "Sim, sair",
      closeActionLabel: "Não, voltar",
      type: "confirm",
    });
  };

  const handleBackButton = () => {
    if (editingWork || editingSocial) {
      const originalWork = userEvaluation?.evaluators?.find(
        (evaluator: any) => evaluator.evaluatorTypeChoice === "Colleague"
      )?.employeeId;
      const originalSocial = userEvaluation?.evaluators?.find(
        (evaluator: any) => evaluator.evaluatorTypeChoice === "Friend"
      )?.employeeId;

      const hasChanged =
        (editingWork && selectedWork !== originalWork) ||
        (editingSocial && selectedSocial !== originalSocial);

      if (!hasChanged) {
        navigate("/home");
        return;
      }
    } else {
      if (!selectedWork && !selectedSocial) {
        navigate("/home");
        return;
      }
    }

    setModalInfo({
      title: "Tens a certeza que queres sair?",
      content:
        editingWork || editingSocial ? (
          <p> Se saires agora, vais perder as alterações.</p>
        ) : (
          <p> Se saires agora, vais perder a seleção de colegas.</p>
        ),
      action: () => {
        navigate("/home");
      },
      closeAction: closeModal,
      confirmActionLabel: "Sim, sair",
      closeActionLabel: "Não, voltar",
      type: "confirm",
    });
  };

  const closeModal = () => {
    setModalInfo(null);
  };

  return (
    <div>
      {loadingUserEvaluation || loadingEmployeesMinimal ? (
        <Loader text="A carregar a avaliação..." />
      ) : (
        <>
          <EvaluationInfo
            evaluation={userEvaluation}
            onBackClick={handleBackButton}
          />

          <div className={`${styles.container} page-container`}>
            {!editingSocial && (
              <div className={styles.work}>
                <div className={styles.titleSection}>
                  <img src={Desktop} alt="Desktop" />
                  <h3 className={`${styles.titleWorkColleague}`}>
                    COLEGA TÉCNICO
                  </h3>
                </div>

                <InviteSelector
                  employees={workEmployeesFiltered}
                  label="Escolhe alguém com quem trabalhas diretamente"
                  selected={selectedWork}
                  onSelect={setSelectedWork}
                />
              </div>
            )}

            {!editingWork && (
              <div className={styles.work}>
                <div className={styles.titleSection}>
                  <img src={Coffee} alt="Coffee" />
                  <h3 className={`${styles.titleSocialColleague}`}>
                    COLEGA SOCIAL
                  </h3>
                </div>

                <InviteSelector
                  employees={socialEmployeesFiltered}
                  label="Escolhe alguém com quem socializas no trabalho"
                  selected={selectedSocial}
                  onSelect={setSelectedSocial}
                />
              </div>
            )}

            <div className={styles.buttons}>
              <WhiteButton
                text={"Fechar e editar mais tarde"}
                className={styles.secondaryButton}
                onClick={handleCloseAndSendLater}
              />
              <BlueButton
                text={"Enviar convites"}
                className={styles.primaryButton}
                onClick={handleModalCreate}
                disabled={
                  !selectedWork ||
                  !selectedSocial ||
                  (Boolean(editingWork) &&
                    selectedWork ===
                      (userEvaluation?.evaluators?.find(
                        (evaluator: any) =>
                          evaluator.evaluatorTypeChoice === "Colleague"
                      )?.employeeId ?? "")) ||
                  (Boolean(editingSocial) &&
                    selectedSocial ===
                      (userEvaluation?.evaluators?.find(
                        (evaluator: any) =>
                          evaluator.evaluatorTypeChoice === "Friend"
                      )?.employeeId ?? ""))
                }
              />
            </div>
          </div>

          {modalInfo && (
            <Modal
              title={modalInfo?.title!}
              type={modalInfo?.type!}
              onConfirmAction={modalInfo?.action!}
              onCloseModal={modalInfo?.closeAction!}
              confirmActionLabel={modalInfo?.confirmActionLabel!}
              closeModalLabel={modalInfo?.closeActionLabel!}
              isSending={modalInfo?.isSending!}
            >
              {modalInfo?.content!}
            </Modal>
          )}
        </>
      )}
    </div>
  );
}
